import { colormaps, ColorMap, COLOR_MODE } from '@ouster/webviz';
import {
  baseRecordingColumnMapping,
  BaseRecordingTableColumn,
  type ColorPaletteName,
} from '@ouster/webviz-react-components';

// TODO (mw) shared between studio and da, move to components or viz

export const MAX_RETURNS = 2;

export const OUSTER_ORG_ID = 1;

export enum WhichReturns {
  BOTH = 'BOTH',
  FIRST_ONLY = 'FIRST_ONLY',
  SECOND_ONLY = 'SECOND_ONLY',
}

export const allColorPalettes: { [key in ColorPaletteName]: ColorMap } = {
  CalRef: colormaps.calRefColorMap,
  Magma: colormaps.magmaColorMap,
  Viridis: colormaps.viridisColorMap,
  Ouster: colormaps.speziaColorMap,
  Greyscale: colormaps.greyscaleColorMap,
  RGB: colormaps.rgbColorMap,
};

export const allColorModes = Object.values(COLOR_MODE);

// Greyscale should be the default palette for 2D images for its
// simplicity/aesthetics. Angus agrees with this.
export const DEFAULT_IMAGE_PALETTE: ColorPaletteName = 'Greyscale';

export const twoDImageOptionsToAdd = {
  [COLOR_MODE.SIGNAL]: {
    showImage: true,
    coloringMode: COLOR_MODE.SIGNAL,
    colorModeOptions: allColorModes,
    colorPalette: DEFAULT_IMAGE_PALETTE,
    colorPaletteOptions: Object.keys(allColorPalettes) as ColorPaletteName[],
    isAutoLevelsOn: true,
    isAutoLevelsDisabled: false,
    isNoiseSuppressionOn: false,
    isNoiseSuppressionDisabled: true,
    returnsSettings: [],
  },
  [COLOR_MODE.NEAR_IR]: {
    showImage: true,
    coloringMode: COLOR_MODE.NEAR_IR,
    colorModeOptions: allColorModes,
    colorPalette: DEFAULT_IMAGE_PALETTE,
    colorPaletteOptions: Object.keys(allColorPalettes) as ColorPaletteName[],
    isAutoLevelsOn: true,
    isAutoLevelsDisabled: false,
    isNoiseSuppressionOn: true,
    isNoiseSuppressionDisabled: false,
    returnsSettings: [],
  },
  [COLOR_MODE.REFLECTIVITY]: {
    showImage: true,
    coloringMode: COLOR_MODE.REFLECTIVITY,
    colorModeOptions: allColorModes,
    colorPalette: 'CalRef',
    colorPaletteOptions: Object.keys(allColorPalettes) as ColorPaletteName[],
    isAutoLevelsOn: true,
    isAutoLevelsDisabled: false,
    isNoiseSuppressionOn: false,
    isNoiseSuppressionDisabled: true,
    returnsSettings: [],
  },
  [COLOR_MODE.RANGE]: {
    showImage: true,
    coloringMode: COLOR_MODE.RANGE,
    colorModeOptions: allColorModes,
    colorPalette: DEFAULT_IMAGE_PALETTE,
    colorPaletteOptions: Object.keys(allColorPalettes) as ColorPaletteName[],
    isAutoLevelsOn: true,
    isAutoLevelsDisabled: false,
    isNoiseSuppressionOn: false,
    isNoiseSuppressionDisabled: true,
    returnsSettings: [],
  },
};

export const WHICH_RETURN_NUMBERS = {
  [WhichReturns.BOTH]: [0, 1],
  [WhichReturns.FIRST_ONLY]: [0],
  [WhichReturns.SECOND_ONLY]: [1],
};

export const ROLES = ['SUPER_ADMIN', 'ADMIN', 'SUPPORT', 'BASIC'] as const;
export const INVITATION_STATUS = ['CREATED', 'ACCEPTED'] as const;
export const LOGIN_APPROACHES = {
  GOOGLE: 'google',
  SSO: 'sso',
  EMAIL: 'email',
};

export const BASE_USER_API = '/api/v1/users';
export const BASE_ORG_API = '/api/v1/organizations';
export const BASE_ONBOARDING_API = '/api/v1/onboarding';
export const BASE_PUBLISHED_RECORDING_API = '/api/v1/published_recordings';
export const BASE_ADMIN_DASHBOARD_API = '/api/v1/organizations/1/dashboard';
export const BASE_UPLOADER_API = 'api/v1/space_util/organizations';

export const TALK_TO_SALE_LINK =
  'https://ouster.com/talk-to-sales?utm_source=ouster_studio&utm_medium=web_app&utm_term={dataset_title}';
export const OUSTER_STUDIO_DOWNLOAD_URL =
  'https://ouster.com/products/software/ouster-studio';

export const MINIMAL_WIDTH = 675;

export const FILTER_KEY_LABEL_MAP = {
  uploaded_by: 'Uploader',
  sdk_prod_type: 'Sensor Type',
  sdk_prod_resolution: 'Sensor Resolution',
  sdk_prod_beam_spacing: 'Beam Spacing',
  sdk_prod_hw_rev: 'Hardware Revision',
  sdk_prod_generation: 'Hardware Generation',
  sdk_prod_sn: 'Serial Number',
  sdk_lidar_mode: 'Mode',
  data_category: 'Category',
  tags: 'Tags',
  created_by: 'Creator',
};

export const DRIVES_PER_PAGE = 200;
export const SCENES_PER_PAGE = 100;

export const SOFT_STORAGE_LIMIT = 250 * 1000 * 1000 * 1000;
export const SINGLE_FILE_LIMIT = 100 * 1000 * 1000 * 1000;

export const GCS_BASE_URL = 'https://console.cloud.google.com/storage/browser';

export const FILTER_GROUPS = [
  {
    title: BaseRecordingTableColumn.UPLOADER,
    filterKeys: [baseRecordingColumnMapping[BaseRecordingTableColumn.UPLOADER]],
  },
  {
    title: BaseRecordingTableColumn.ORGANIZATION,
    filterKeys: [
      baseRecordingColumnMapping[BaseRecordingTableColumn.ORGANIZATION],
    ],
  },
  {
    title: BaseRecordingTableColumn.SHARED_BY,
    filterKeys: [
      baseRecordingColumnMapping[BaseRecordingTableColumn.SHARED_BY],
    ],
  },
  {
    title: BaseRecordingTableColumn.SENSOR,
    filterKeys: [
      baseRecordingColumnMapping[BaseRecordingTableColumn.SENSOR],
      baseRecordingColumnMapping[BaseRecordingTableColumn.BEAM_SPACING],
      baseRecordingColumnMapping[BaseRecordingTableColumn.RESOLUTION],
    ],
  },
  {
    title: BaseRecordingTableColumn.HW,
    filterKeys: [
      baseRecordingColumnMapping[BaseRecordingTableColumn.HW],
      baseRecordingColumnMapping[BaseRecordingTableColumn.PRODUCT_GENERATION],
    ],
  },
  {
    title: BaseRecordingTableColumn.SN,
    filterKeys: [baseRecordingColumnMapping[BaseRecordingTableColumn.SN]],
  },
  {
    title: BaseRecordingTableColumn.FW,
    filterKeys: [baseRecordingColumnMapping[BaseRecordingTableColumn.FW]],
  },
  {
    title: BaseRecordingTableColumn.MODE,
    filterKeys: [baseRecordingColumnMapping[BaseRecordingTableColumn.MODE]],
  },
  {
    title: BaseRecordingTableColumn.CATEGORY,
    filterKeys: [baseRecordingColumnMapping[BaseRecordingTableColumn.CATEGORY]],
  },
  {
    title: BaseRecordingTableColumn.TAGS,
    filterKeys: [baseRecordingColumnMapping[BaseRecordingTableColumn.TAGS]],
  },
];

export const EXCLUDED_COLOR_MODES = [
  'FLAGS',
  'FLAGS2',
  'RANGE2',
  'REFLECTIVITY2',
  'SIGNAL2',
];

export const EXCLUDED_IMAGE_COLOR_MODES = ['FLAGS', 'FLAGS2'];
